/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import wrapWithProvider from "./src/wrap-with-provider";

// ─────────────────────────────────────────────────────────────────────────────
// Mobile App Download — Deep Link / Store Redirect
// ─────────────────────────────────────────────────────────────────────────────
//
// Handles the route  /sonesta-mobile-app-download?src=sfmc  coming from
// Salesforce Marketing Cloud (SFMC) email campaigns.
//
// ┌──────────┬──────────────────────────────────────────────────────────────┐
// │ Platform │ Behaviour                                                    │
// ├──────────┼──────────────────────────────────────────────────────────────┤
// │ Desktop  │ No action — page loads normally (no dedicated page exists,   │
// │          │ so the user will see the Gatsby 404).                        │
// ├──────────┼──────────────────────────────────────────────────────────────┤
// │ iOS      │ 1. Inject Smart App Banner (Apple's recommended approach)    │
// │          │ 2. Attempt custom-scheme deep link  sonesta://offers         │
// │          │ 3. Fallback → App Store after timeout                        │
// ├──────────┼──────────────────────────────────────────────────────────────┤
// │ Android  │ Chrome → intent:// URI with S.browser_fallback_url (native)  │
// │ (Chrome) │ Fallback handled natively by Chrome — no timeouts needed.    │
// ├──────────┼──────────────────────────────────────────────────────────────┤
// │ Android  │ Custom scheme → Play Store fallback via timeout.             │
// │ (Other)  │                                                              │
// └──────────┴──────────────────────────────────────────────────────────────┘
//
// References:
//   • Smart App Banner  — https://developer.apple.com/documentation/webkit/promoting_apps_with_smart_app_banners
//   • Chrome Intents    — https://developer.chrome.com/docs/android/intents
//   • Android Deep Link — https://developer.android.com/training/app-links/deep-linking
//
// Future improvements:
//   • Configure Universal Links (iOS) with apple-app-site-association on the server
//     to enable seamless app opening without custom-scheme error dialogs.
//     → https://developer.apple.com/documentation/xcode/supporting-universal-links-in-your-app
//   • Configure Android App Links with assetlinks.json to bypass disambiguation dialogs.
//     → https://developer.android.com/training/app-links/verify-applinks
// ─────────────────────────────────────────────────────────────────────────────

/** Sonesta mobile app identifiers and store URLs (immutable). */
const APP_CONFIG = Object.freeze({
  ios: Object.freeze({
    appId: "1605980384",
    appStoreURL: "https://apps.apple.com/us/app/sonesta/id1605980384",
    deepLink: "sonesta://offers",
  }),
  android: Object.freeze({
    packageName: "sihc.android.sonestaapp",
    playStoreURL:
      "https://play.google.com/store/apps/details?id=sihc.android.sonestaapp",
    deepLink: "sonesta://offers",
  }),
});

/** Time (ms) to wait for the deep link to respond before falling back to the store. */
const IOS_DEEP_LINK_TIMEOUT_MS = 2500;

/** Time (ms) for non-Chrome Android browsers before redirecting to Play Store. */
const ANDROID_NON_CHROME_TIMEOUT_MS = 1500;

// ── Platform detection ───────────────────────────────────────────────────────

/**
 * Detect the user's mobile platform.
 *
 * Notes:
 *  - iPadOS 13+ reports as "MacIntel" in the UA — we detect it via
 *    maxTouchPoints. This ensures iPad users also get the redirect.
 *  - We no longer rely on the deprecated `navigator.vendor`, `window.opera`
 *    or the IE-only `window.MSStream`.
 *
 * @returns {{ isIOS: boolean, isAndroid: boolean, isMobile: boolean }}
 */
const detectPlatform = () => {
  const ua = navigator.userAgent;

  const isIOS =
    /iP(hone|od|ad)/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const isAndroid = /Android/i.test(ua);

  return Object.freeze({ isIOS, isAndroid, isMobile: isIOS || isAndroid });
};

/**
 * Check whether the current browser is Chrome on Android.
 * Excludes Chrome-based browsers (Edge, Opera, Samsung Internet) which do
 * NOT support the `intent://` URI scheme reliably.
 */
const isChromeOnAndroid = () => {
  const ua = navigator.userAgent;
  return (
    /Chrome\/[\d.]+/.test(ua) &&
    !/EdgA?\/|OPR\/|Opera\/|SamsungBrowser\//.test(ua)
  );
};

// ── Navigation detector ──────────────────────────────────────────────────────

/**
 * Create a lightweight observer that tracks whether the user has navigated away
 * from the current page (i.e. the deep link succeeded or we redirected to a
 * store).  Listens for both `visibilitychange` and `pagehide` for coverage
 * across all mobile browsers.
 *
 * `blur` was intentionally removed — it triggers false positives (keyboard
 * dismiss, pull-down notification tray, etc.) and doesn't reliably indicate
 * navigation on mobile.
 *
 * @returns {{ hasNavigatedAway: () => boolean, cleanup: () => void }}
 */
const createNavigationDetector = () => {
  let navigatedAway = false;

  const onVisibilityChange = () => {
    if (document.hidden) {
      navigatedAway = true;
      cleanup();
    }
  };

  const onPageHide = () => {
    navigatedAway = true;
    cleanup();
  };

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);

  return { hasNavigatedAway: () => navigatedAway || document.hidden, cleanup };
};

// ── iOS handling ─────────────────────────────────────────────────────────────

/**
 * Inject Apple Smart App Banner meta tag.
 *
 * This is Apple's officially recommended promotion mechanism:
 *  - App installed  → "OPEN" button that opens the app (with `app-argument`).
 *  - App not installed → "GET" button linking to the App Store.
 *  - Automatically hides if the device doesn't support the app.
 *  - User-dismissible; won't reappear once dismissed.
 *
 * @see https://developer.apple.com/documentation/webkit/promoting_apps_with_smart_app_banners
 */
const injectSmartAppBanner = () => {
  if (document.querySelector('meta[name="apple-itunes-app"]')) {
    return;
  }

  const meta = document.createElement("meta");
  meta.name = "apple-itunes-app";
  meta.content = `app-id=${APP_CONFIG.ios.appId}, app-argument=${APP_CONFIG.ios.deepLink}`;
  document.head.appendChild(meta);
};

/**
 * iOS redirect handler.
 *
 * Strategy (in order):
 *  1. Inject Smart App Banner — passive but reliable Apple-native fallback.
 *  2. Navigate to the custom scheme `sonesta://offers`.
 *     • If the app IS installed → iOS opens the app immediately;
 *       the `visibilitychange` event fires and we cancel the fallback timer.
 *     • If the app is NOT installed → Safari may briefly show an
 *       "invalid address" dialog (unavoidable without Universal Links).
 *  3. After `IOS_DEEP_LINK_TIMEOUT_MS` with no navigation → redirect to
 *     the App Store.  The App Store page shows "OPEN" if the app is already
 *     installed, or "GET" if not.
 *
 * ⚠  The hidden-iframe technique (`iframe.src = scheme`) was intentionally
 *    removed — Apple blocked custom-scheme iframes since iOS 9 (2015).
 *    Using it today is a no-op that adds dead code.
 *
 * 💡 To eliminate the "invalid address" dialog entirely, configure Universal
 *    Links with an `apple-app-site-association` file on the web server.
 *    @see https://developer.apple.com/documentation/xcode/supporting-universal-links-in-your-app
 */
const handleIOSRedirect = () => {
  injectSmartAppBanner();

  const detector = createNavigationDetector();

  // Attempt to open the app via custom scheme.
  window.location.href = APP_CONFIG.ios.deepLink;

  // Fallback: if the app didn't respond, send the user to the App Store.
  setTimeout(() => {
    detector.cleanup();
    if (!detector.hasNavigatedAway()) {
      window.location.href = APP_CONFIG.ios.appStoreURL;
    }
  }, IOS_DEEP_LINK_TIMEOUT_MS);
};

// ── Android handling ─────────────────────────────────────────────────────────

/**
 * Build a Chrome `intent://` URI for the given app parameters.
 *
 * Format (per Chrome docs):
 *   intent://HOST#Intent;scheme=SCHEME;package=PACKAGE;S.browser_fallback_url=URL;end
 *
 * Chrome natively handles the fallback:
 *  - App installed → opens the app, no JS-side timeout needed.
 *  - App NOT installed → Chrome redirects to `browser_fallback_url`.
 *  - JS-initiated without user gesture → Chrome redirects to fallback URL
 *    instead of launching external app (safe degradation).
 *
 * @see https://developer.chrome.com/docs/android/intents
 *
 * @param {string} deepLink     – e.g. "sonesta://offers"
 * @param {string} packageName  – e.g. "sihc.android.sonestaapp"
 * @param {string} fallbackURL  – Play Store URL
 * @returns {string|null} The intent URI, or null if `deepLink` is malformed.
 */
const buildIntentURL = (deepLink, packageName, fallbackURL) => {
  // Extract scheme and host+path from the deep link.
  // "sonesta://offers"  →  scheme = "sonesta",  hostPath = "offers"
  const match = deepLink.match(/^(\w+):\/\/(.+)$/);
  if (!match) {
    return null;
  }

  const [, scheme, hostPath] = match;

  return (
    `intent://${hostPath}#Intent;` +
    `scheme=${scheme};` +
    `package=${packageName};` +
    `S.browser_fallback_url=${encodeURIComponent(fallbackURL)};` +
    `end`
  );
};

/**
 * Android redirect handler.
 *
 * Chrome (majority of Android users):
 *   Uses the `intent://` URI with `S.browser_fallback_url`.
 *   Chrome resolves everything natively — no setTimeout fallback required.
 *
 * Non-Chrome (Samsung Internet, Firefox, etc.):
 *   Attempts the custom scheme directly.  If the page is still visible after
 *   `ANDROID_NON_CHROME_TIMEOUT_MS`, we redirect to the Play Store.
 *
 * @see https://developer.chrome.com/docs/android/intents
 * @see https://developer.android.com/training/app-links/deep-linking
 */
const handleAndroidRedirect = () => {
  const { packageName, playStoreURL, deepLink } = APP_CONFIG.android;

  if (isChromeOnAndroid()) {
    // Chrome's native intent:// approach — handles fallback internally.
    const intentURL = buildIntentURL(deepLink, packageName, playStoreURL);
    window.location.href = intentURL || playStoreURL;
    return;
  }

  // Non-Chrome: custom scheme with manual Play Store fallback.
  const detector = createNavigationDetector();

  window.location.href = deepLink;

  setTimeout(() => {
    detector.cleanup();
    if (!detector.hasNavigatedAway()) {
      window.location.href = playStoreURL;
    }
  }, ANDROID_NON_CHROME_TIMEOUT_MS);
};

// ── Main handler ─────────────────────────────────────────────────────────────

/**
 * Entry point — called on every Gatsby client page load.
 * Exits immediately for any route / query that doesn't match the SFMC
 * deep-link pattern.
 */
const handleMobileAppDownloadRedirect = () => {
  // Normalise the pathname (strip trailing slashes for consistent matching).
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";

  if (pathname !== "/sonesta-mobile-app-download") {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get("src") !== "sfmc") {
    return;
  }

  const { isIOS, isAndroid, isMobile } = detectPlatform();

  // Desktop users: no redirect (the page will render its normal content / 404).
  if (!isMobile) {
    return;
  }

  if (isIOS) {
    handleIOSRedirect();
  } else if (isAndroid) {
    handleAndroidRedirect();
  }
};

// ── Gatsby Browser API exports ───────────────────────────────────────────────

export const onClientEntry = () => {
  handleMobileAppDownloadRedirect();
};

export const wrapRootElement = wrapWithProvider;
